import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { Package } from 'lucide-react';

import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import { ITEM_TYPE_META, fmtDate, soft, amountText } from '../../screens/Accounting/Accounting.shared';
import { accountingLineLabel } from '../../lib/ledgerVocabulary';

// One accounting line as a mobile card (DESIGN-CONCEPT-MOBILE v2 §5). A ledger
// entry is a read — no tap target — so it renders as a plain card, not a Button:
// type badge + settlement pill on top, the service description, then a meta line
// (date · clinic · patient) and the two money figures (کل · سهم شما), both in the
// line's own invoice currency (`line.currency`; blank reads IRR).

const TypeBadge = ({ itemType }) => {
  const { t } = useTranslation();
  const m = ITEM_TYPE_META(t)[itemType] || { label: itemType, Icon: Package, color: C.textSecondary };
  const Icon = m.Icon;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
        borderRadius: '0.375rem', padding: '0.1875rem 0.5rem',
        fontSize: '0.6875rem', fontWeight: 700, whiteSpace: 'nowrap',
        background: soft(m.color, 0.1), color: m.color,
      }}
    >
      <Icon size={12} aria-hidden />{m.label}
    </span>
  );
};

const MobileAccountingLineCard = ({ line, testId }) => {
  const { t, formatDigits, formatDate, formatMoney, moneyUnit } = useTranslation();
  const { currency } = line;
  return (
  <div
    data-testid={testId}
    style={{
      background: 'white', borderRadius: '0.875rem',
      border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      padding: '0.75rem 0.875rem',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
      <TypeBadge itemType={line.itemType} />
      <span style={{ flex: 1 }} />
      <StatusChip status={line.settlementStatus} />
    </div>

    {/* Server-written Persian — see the note in Accounting.web.jsx. */}
    <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary, marginBottom: '0.25rem' }}>
      {accountingLineLabel(line.description, t)}
      {line.quantity > 1 && (
        <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: C.textMuted, marginInlineStart: '0.25rem' }}>×{formatDigits(line.quantity)}</span>
      )}
    </div>

    <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginBottom: '0.5rem' }}>
      {fmtDate(line.createdAt, formatDate)} · {line.clinicName || t('accounting.noClinic')}
      {line.patientName && ` · ${line.patientName}`}
    </div>

    <div style={{ display: 'flex', gap: '0.875rem', fontSize: '0.75rem' }}>
      <span style={{ color: C.textMuted }}>
        {t('accounting.totalLabel')} <b style={{ color: C.textPrimary }}>{formatMoney(line.grossAmount, { currency })} {moneyUnit(currency, 'short')}</b>
      </span>
      <span style={{ color: C.teal }}>
        {t('accounting.yourShareLabel')} <b>{line.doctorShare == null ? '—' : amountText(line.doctorShare, currency, t, formatMoney, moneyUnit)}</b>
      </span>
    </div>
  </div>
);
                                 };

export default MobileAccountingLineCard;
